import React, { useEffect, useRef, useState } from "react";
import { ScrollView, StyleSheet, Text, View, ViewProps } from "react-native";
import { Image } from "expo-image";
import { CardsContentData } from "../providers/ConfigProvider";
import { AbsoluteView } from "./AbsoluteView";
import RenderHtml from "react-native-render-html";
import { config } from "../config";
import { ThemeOptions, useTheme } from "../providers/ThemeProvider";

const makeStyles = (theme: ThemeOptions) =>
  StyleSheet.create({
    wrapper: {
      padding: theme.scaled(2),
      flexDirection: "row",
    },
    scrollViewContainer: {
      flexGrow: 1,
      justifyContent: "center",
    },
    markdown: {
      paddingHorizontal: theme.spacing.medium,
      color: theme.backgroundText,
      fontSize: theme.fontSize.body,
      lineHeight: theme.fontSize.large,
    },
    cardImage: {
      height: "90%",
      width: theme.iconSize.xlarge,
      resizeMode: "contain",
    },
    imageContainer: {
      paddingHorizontal: theme.spacing.xsmall,
      justifyContent: "center",
    },
    titleText: {
      marginHorizontal: theme.spacing.medium,
      paddingBottom: theme.spacing.xsmall,
      color: theme.backgroundText,
      fontWeight: "600",
      fontSize: theme.fontSize.large,
    },
  });

export const TextCards = ({
  data,
  onLoopComplete,
  ...props
}: ViewProps & {
  data?: CardsContentData;
  onLoopComplete?: () => void;
}) => {
  const theme = useTheme();
  const styles = makeStyles(theme);
  const scrollAnimation = useRef(0);
  const scrollView = useRef<ScrollView>(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [outerHeight, setOuterHeight] = useState<number>();
  const [innerHeight, setInnerHeight] = useState<number>();
  const [contentWidth, setContentWidth] = useState<number>();

  useEffect(() => {
    // if cards have been updated, and the current index no longer exists
    if (currentIndex >= (data?.cards ?? []).length) {
      setCurrentIndex(0);
    }

    // the layout hasn't completed yet
    if (innerHeight == null || outerHeight == null) {
      return;
    }

    // ignore tiny overflows caused by measurement/layout noise, not real content overflow
    const rawOverflow = innerHeight - outerHeight;
    const overflowThreshold = theme.scaled(5);

    // distance the card needs to scroll to reach the bottom
    const toValue = rawOverflow > overflowThreshold ? rawOverflow : 0;

    let interval: NodeJS.Timeout;
    let timeout: NodeJS.Timeout;

    const startScrolling = () => {
      scrollAnimation.current = 0;
      scrollView.current?.scrollTo({ y: 0, animated: false });

      timeout = setTimeout(() => {
        interval = setInterval(() => {
          if (scrollAnimation.current >= toValue) {
            clearInterval(interval);

            timeout = setTimeout(() => {
              if ((data?.cards ?? []).length === 1) {
                startScrolling(); // Restart the loop for a single card
              } else {
                const nextIndex =
                  currentIndex + 1 >= (data?.cards ?? []).length
                    ? 0
                    : currentIndex + 1;
                setCurrentIndex(nextIndex);
                if (nextIndex === 0) onLoopComplete?.();
                scrollAnimation.current = 0;
                scrollView.current?.scrollTo({ y: 0, animated: false });
              }
            }, config.textCards.endDelay);
          } else {
            scrollAnimation.current += Math.max(theme.scaled(1), 1);
            scrollView.current?.scrollTo({
              y: scrollAnimation.current,
              animated: false,
            });
          }
        }, 1000 / config.textCards.fps);
      }, config.textCards.startDelay);
    };

    startScrolling();

    return () => {
      clearTimeout(timeout);
      clearInterval(interval);
    };
  }, [innerHeight, outerHeight, currentIndex, data, onLoopComplete, theme]);

  const currentCard = (data?.cards ?? [])[currentIndex];

  if (currentCard == null) {
    return null;
  }

  return (
    <AbsoluteView {...props} contentStyle={styles.wrapper}>
      {currentCard.imageUrl != null && (
        <View style={styles.imageContainer}>
          <Image
            style={styles.cardImage}
            source={{ uri: currentCard.imageUrl }}
            contentFit="contain"
          />
        </View>
      )}
      <ScrollView
        ref={scrollView}
        contentContainerStyle={styles.scrollViewContainer}
        showsVerticalScrollIndicator={false}
        onLayout={(ev) => setOuterHeight(ev.nativeEvent.layout.height)}
      >
        <View
          onLayout={(ev) => {
            setInnerHeight(ev.nativeEvent.layout.height);
            setContentWidth(ev.nativeEvent.layout.width);
          }}
        >
          {contentWidth != null && (
            <>
              {currentCard.title != null && (
                <Text style={styles.titleText}>{currentCard.title}</Text>
              )}
              <RenderHtml
                source={{ html: currentCard.text ?? "" }}
                contentWidth={contentWidth}
                baseStyle={{
                  color: theme.backgroundText,
                  paddingHorizontal: theme.spacing.medium,
                  fontSize: theme.fontSize.body,
                }}
                tagsStyles={{
                  p: { margin: 0 },
                  br: { margin: 0 },
                  div: { minHeight: theme.fontSize.body },
                }}
              />
            </>
          )}
        </View>
      </ScrollView>
    </AbsoluteView>
  );
};
